import { useMemo } from 'react';
import * as yup from 'yup';

/** `days[0].opensAt` and `card.titleRo` alike belong to their first segment. */
const rootOf = (path: string | undefined): string => (path ?? '').replace(/[.[].*$/, '');

/**
 * Whether the fields one wizard step owns are all satisfied, given the whole form.
 *
 * <p>The whole object is validated, not the slice, because a rule may be about two fields:
 * an evaluation's `childAge` is required only when `consentCapacity` says a legal
 * representative is signing, and a per-field check could not see the other one.
 * `yup.reach` has the same blind spot -- it returns the *unconditioned* base schema for a
 * field carrying `.when()`, and would call a missing child age valid.
 *
 * <p>What is sliced is the *answer*: a failure on the signature of the data-processing
 * agreement is not this step's business while the therapist is still on the first one.
 * That is the whole point -- `formState.isValid` is whole-form, so a Continue button wired
 * to it would be dead on step one for ever.
 *
 * <p>Pure, and synchronous by design. `validateSync` throws if any rule returns a promise
 * and none in this app does; a rule that has to await something belongs on the server,
 * which re-checks all of this anyway.
 */
export const stepIsValid = (
    schema: yup.ObjectSchema<any>,
    values: unknown,
    fields: readonly string[]
): boolean => {
    const owned: Set<string> = new Set(fields);

    try {
        schema.validateSync(values, { abortEarly: false });
        return true;
    } catch (error: unknown) {
        if (!(error instanceof yup.ValidationError)) {
            throw error;
        }

        // A single failure arrives on the error itself, with an empty `inner`.
        const issues: yup.ValidationError[] = error.inner.length > 0 ? error.inner : [error];

        return !issues.some((issue) => owned.has(rootOf(issue.path)));
    }
};

/**
 * {@link stepIsValid}, memoised on the values react-hook-form last handed out.
 *
 * <p>It never writes to `formState`, so asking cannot make a message appear early. That
 * separation from `trigger` -- which does write, and would paint an untouched step red the
 * moment it mounted -- is what lets a disabled button coexist with validate-on-blur.
 */
export const useStepValidity = (
    schema: yup.ObjectSchema<any>,
    values: unknown,
    fields: readonly string[]
): boolean => useMemo(() => stepIsValid(schema, values, fields), [schema, values, fields]);
